import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAPI } from '../../API';
import { useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import { useRefresh } from '../../Hooks/useRefresh';
import SegmentControl, { useTabs } from '../../Components/SegmentControl';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { hoverTextColor } from '../../Resources/CSS/styling';
import Select from '../../Components/Inputs/Select';
import { TextInput } from '../../Components/Inputs/TextInput';
import { Button } from '../../Components/Buttons';
import DateRangeSelection, { getDatesFromRange } from '../../Components/Inputs/DateRangeSelection';
import { LineChart } from '../../Components/Charts/LineChart.tsx';
import { BarChart } from '../../Components/Charts/BarChart.tsx';

const useResults = (results, report) => {
    const columns = report?.type === 'insight' ? ['letter_identifier', 'result', 'count', 'avg_per_contact', 'unique_contacts', 'median_per_contact'] : Object.keys(results).map(key => ({
        key: key,
        label: key,
    }));

    var data = Object.keys(results).map((key) => {
        return {
            key: key,
            ...results[key],
        }});

    if (report?.type === 'funnel') {
        data = [{}];

        for (const key of Object.keys(results)) {
            data[0][key] = results[key].contact_count;
        }
    }

    console.log("data", data);
    console.log("columns", columns);

    return {
        column: columns,
        data: data 
    };
}


export default function Report() {

    const { translate, language, setLanguage } = useTranslation();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const api = useAPI();

    const appState = useAppState();
    const dispatch = useAppDispatch();
    const { sidebarSearch } = appState;
    const [report, setReport] = useState(null);
    const [results, setResults] = useState({});
    const [eventNames, setEventNames] = useState([]);
    const [tmpReport, setTmpReport] = useState(null);

    const reportTypes = useTabs([
        { name: translate('reports.insights'), type: 'insight', current: false },
        { name: translate('reports.funnel'), type: 'funnel', current: false }
    ]);

    const displayTypes = useMemo(() => [
        { name: translate('reports.metric'), type: 'metric' },
        ...(tmpReport?.type !== 'funnel' ? [{ name: translate('reports.line'), type: 'line' }] : []),
        ...(tmpReport?.type !== 'funnel' ? [{ name: translate('reports.bar'), type: 'bar' }] : []),
        ...(tmpReport?.type === 'funnel' ? [{ name: translate('reports.bar_funnel'), type: 'bar_funnel' }] : []),
    ], [tmpReport]);

    useEffect(() => {
        if (tmpReport && tmpReport.display_type && !displayTypes.find((displayType) => displayType.type === tmpReport.display_type)) {
            setTmpReport({ ...tmpReport, display_type: displayTypes[0].type });
        }
    }, [displayTypes]);

    const { formatDate } = useDateFormat();

    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();

    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const pagination = usePaginations();
    const { refresh, refreshId } = useRefresh();
    const reportId = useParams().reportId;

    useEffect(() => {
        setLoading(true);
        api.getReport(reportId, fromDate ? formatDate(fromDate, "yyyy-MM-dd", 0) : undefined, toDate ? formatDate(toDate, "yyyy-MM-dd", 0) : undefined).then((response) => {
            reportTypes.setCurrentTab(reportTypes.tabs.find(type => type.type === response.data.report.type));
            setReport(response.data.report);
            setTmpReport(response.data.report);
            setResults(response.data.results);

            const { fromDate: defaultFromDate, toDate: defaultToDate } = getDatesFromRange(response.data.report.default_date_range || 'thirty_days');
            setFromDate(defaultFromDate);
            setToDate(defaultToDate);

            api.listEventNames().then((response) => {
                const eventNames = response.data.event_names.map((eventName, index) => ({
                    id: index,
                    label: `${eventName}`, // Use label to avoid translation issues
                    value: eventName
                }));
                setEventNames(eventNames);
                setLoading(false);
            });
        });


    }, [refreshId]);

    const setToDateEndOfDay = (date) => {
        // Create date in local timezone to avoid timezone offset issues
        const [year, month, day] = date.split('-').map(Number);
        const newDate = new Date(year, month - 1, day, 0, 0, 0);
        setToDate(newDate);
        console.log("toDate", newDate);
    }

    const setFromDateStartOfDay = (date) => {
        // Create date in local timezone to avoid timezone offset issues
        const [year, month, day] = date.split('-').map(Number);
        const newDate = new Date(year, month - 1, day, 0, 0, 0);
        setFromDate(newDate);
        console.log("fromDate", newDate);
    }

    const setReportType = (type) => {
        setTmpReport({ ...tmpReport, type: type.type });
    }

    const handleSave = (reportUpdated) => {
        const reportToSave = reportUpdated || tmpReport;
        if (reportToSave.id) {
            setLoading(true);
            api.updateReport(reportToSave).then((response) => {
                refresh();
            });
        } else { }
    }

    const getNextLetterIdentifier = (report) => {
        if (report.data.length === 0) {
            return 'A';
        }
        const lastLetterIdentifier = report.data[report.data.length - 1].letter_identifier;
        return String.fromCharCode(lastLetterIdentifier.charCodeAt(0) + 1);
    }

    const handleAddMetric = () => {
        setTmpReport({ ...tmpReport, data: [...tmpReport.data, { letter_identifier: getNextLetterIdentifier(tmpReport), type: 'event', name: '', description: '', information: 'EVENTS_COUNT' }] });
    }

    const handleRemoveMetric = (metric) => {
        setTmpReport({ ...tmpReport, data: tmpReport.data.filter((m) => m.letter_identifier !== metric.letter_identifier) });
    }

    const setDisplayType = (type) => {
        const reportUpdated = { ...report, display_type: type.type };
        handleSave(reportUpdated);
    }

    const { data: resultsData, column: resultsColumns } = useResults(results, report);
    

    const setDateRange = (value) => {
        setFromDate(value.fromDate);
        setToDate(value.toDate);

        const reportUpdated = { ...report, default_date_range: value.defaultDateRange };
        setTmpReport(reportUpdated);
        handleSave(reportUpdated);
    }

    useEffect(() => {
        if (reportTypes.currentTab) {
            setTmpReport({ ...tmpReport, type: reportTypes.currentTab.type });
        }
        
    }, [reportTypes.currentTab]);

    const isMonthly = ['three_months', 'six_months', 'one_year'].includes(report?.default_date_range);

    const funnelWindowOptions = [
        { name: translate('reports.funnel_window_minutes'), value: 'minutes', coefficient: 1 },
        { name: translate('reports.funnel_window_hours'), value: 'hours', coefficient: 60 },
        { name: translate('reports.funnel_window_days'), value: 'days', coefficient: 1440 },
        { name: translate('reports.funnel_window_weeks'), value: 'weeks', coefficient: 10080 },
        { name: translate('reports.funnel_window_months'), value: 'months', coefficient: 43200 },
    ];

    const selectedWindowUnit = funnelWindowOptions.find((funnelWindowOption) => funnelWindowOption.value === tmpReport?.funnel_window_unit) || funnelWindowOptions[0];

    const handleChangeFunnelWindow = (value) => {
        
        setTmpReport({ ...tmpReport, funnel_window_minutes: value * selectedWindowUnit.coefficient });
    }

    const handleChangeFunnelWindowUnit = (value) => {
        const oldWindowUnit = funnelWindowOptions.find((funnelWindowOption) => funnelWindowOption.value === tmpReport.funnel_window_unit) || funnelWindowOptions[0];
        setTmpReport({ ...tmpReport, funnel_window_minutes: tmpReport.funnel_window_minutes * value.coefficient / oldWindowUnit.coefficient, funnel_window_unit: value.value });
    }

    const funnelWindowValue = tmpReport?.funnel_window_minutes / selectedWindowUnit.coefficient;


    return (
        <>
            <div className="flex min-h-full w-full">
                <SidebarNav contentPadding={false} currentPage="reports">
                    {loading ? <div className="flex justify-center items-center h-full w-12 m-8">
                        <LoadingCircleTransparent />
                    </div> : (
                        <div className="min-h-screen pb-16 flex flex-col w-full">
                            <div className="flex flex-row w-full flex-1">
                                <div className="flex flex-col w-3/4 max-h-screen">
                                    <div className="flex flex-col w-full flex-1 p-8">
                                        <div className="flex flex-row justify-between items-center">
                                            <div className="flex flex-row items-center gap-2 justify-end">
                                                <DateRangeSelection value={{ fromDate, toDate, defaultDateRange: report.default_date_range || 'thirty_days' }} onChange={(value) => setDateRange(value)} />
                                                {/* <div className="flex flex-row justify-end">
                                                    <Button  onClick={() => {
                                                        refresh();
                                                    }}>{translate('events.fetch')}</Button>
                                                </div> */}
                                            </div>
                                            <div className="flex flex-col w-32">
                                                <Select options={displayTypes} selected={displayTypes.find((displayType) => displayType.type === report.display_type) || displayTypes[0]} setSelected={(e) => setDisplayType(e)} />
                                            </div>

                                        </div>
                                        <ReportChart report={report} results={results} isMonthly={isMonthly} />
                                    </div>
                                    <div className="flex flex-col w-full h-96 border-t border-gray-200 pl-16">
                                        <Table
                                            createButton={undefined}
                                            data={resultsData}
                                            translationKey="reports.table"
                                            columns={resultsColumns}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col w-1/4 border-l border-gray-200">
                                    <SegmentControl
                                        tabs={reportTypes}
                                    />

                                    {reportTypes.currentTab.type === 'funnel' && <div className="flex flex-col w-full">
                                        <div className="flex flex-row mt-8 mx-4 justify-between items-center">
                                            <p className="text-gray-700 text-xl font-bold">{translate('reports.funnel_window')}</p>
                                            
                                        </div>
                                        <div className="flex flex-row mx-4 space-x-2">
                                            <div className="flex flex-col w-24">
                                                <TextInput type="number" placeholder={translate('reports.funnel_window_placeholder')}  value={funnelWindowValue} onChange={(e) => handleChangeFunnelWindow(e.target.value)} />    
                                            </div>
                                            <div className="flex flex-col w-48">
                                                <Select options={funnelWindowOptions} selected={funnelWindowOptions.find((funnelWindowOption) => funnelWindowOption.value === tmpReport.funnel_window_unit) || funnelWindowOptions[0]} setSelected={(e) => handleChangeFunnelWindowUnit(e)} />
                                            </div>
                                        </div>
                                    </div>}
                                    <div className="flex flex-row mt-8 mx-4 justify-between items-center">
                                        <p className="text-gray-700 text-xl font-bold">{translate('reports.metrics')}</p>
                                        <PlusIcon onClick={() => handleAddMetric()} className={`w-8 h-8 text-gray-700 cursor-pointer ${hoverTextColor}`} />
                                    </div>

                                    <ReportMetrics report={tmpReport} setReport={setTmpReport} eventNames={eventNames} save={handleSave} handleRemoveMetric={handleRemoveMetric} />
                                </div>
                            </div>


                        </div>
                    )}
                </SidebarNav>
            </div>
        </>
    );
}

const ReportMetrics = ({ report, eventNames, setReport, save, handleRemoveMetric }) => {
    const { translate } = useTranslation();

    const handleSelectEventName = (metric, value) => {
        metric.name = value.value;
        setReport({ ...report });
    }

    const handleChangeDescription = (metric, value) => {
        metric.description = value;
        setReport({ ...report });
    }

    const handleSelectInformation = (metric, value) => {
        metric.result_value = value.value;
        setReport({ ...report });
    }

    const informationOptions = [
        { name: translate('reports.events_count'), value: 'EVENTS_COUNT' },
        { name: translate('reports.unique_contacts'), value: 'UNIQUE_CONTACTS' },
        { name: translate('reports.average_per_contact'), value: 'AVERAGE_PER_CONTACT' },
        { name: translate('reports.median_per_contact'), value: 'MEDIAN_PER_CONTACT' },
    ]
    return (
        <div className="flex flex-col w-full pb-64">
            {report.type === 'insight' ? (
                <div className="flex flex-col w-full space-y-4 pt-4">
                    {report.data.map((metric, index) => (
                        <div key={metric.name + index} className="flex flex-col mx-4 p-2 border border-gray-200 rounded-md">
                            <div className="flex flex-row w-full items-center">
                                <div className="flex flex-row w-8 h-8 bg-gray-50 border border-gray-200 rounded-md items-center justify-center"  >
                                    <span className="text-sm font-bold">{metric.letter_identifier}</span>
                                </div>
                                <div className="w-full flex flex-col">
                                    <TextInput placeholder={translate('reports.description_placeholder')} className="outline-0 border-0 outline-none focus:outline-0 font-medium sm:text-lg -mt-2 w-full" value={metric.description} onChange={(e) => handleChangeDescription(metric, e.target.value)} />
                                </div>
                                <MinusIcon onClick={() => handleRemoveMetric(metric)} className="w-6 h-6 text-red-500 cursor-pointer ml-2 hover:text-red-400" />
                            </div>
                            <div className="flex flex-row w-full items-center gap-2">
                                <div className="flex flex-col w-full border-t border-gray-200 mt-2 pt-2">
                                    <p className="text-gray-700 text-sm font-bold">{translate('reports.event')}</p>
                                    <Select options={eventNames} selected={eventNames.find((eventName) => eventName.value === metric.name) || eventNames[0]} setSelected={(e) => handleSelectEventName(metric, e)} />
                                </div>
                                <div className="flex flex-col w-full border-t border-gray-200 mt-2 pt-2">
                                    <p className="text-gray-700 text-sm font-bold">{translate('reports.result_value')}</p>
                                    <Select options={informationOptions} selected={informationOptions.find((information) => information.value === metric.result_value) || informationOptions[0]} setSelected={(e) => handleSelectInformation(metric, e)} />
                                </div>
                            </div>

                            <MetricFilters report={report} setReport={setReport} metric={metric} index={index} />
                        </div>
                    ))}
                    <div className="flex flex-row justify-end mt-4 mx-4">
                        <Button onClick={() => save()}>{translate('save')}</Button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col w-full space-y-4 pt-4">
                    {report.data.map((metric, index) => (
                        <div key={metric.name + index} className="flex flex-col mx-4 p-2 border border-gray-200 rounded-md">
                            <div className="flex flex-row w-full items-center">
                                <div className="flex flex-row w-8 h-8 bg-gray-50 border border-gray-200 rounded-md items-center justify-center"  >
                                    <span className="text-sm font-bold">{metric.letter_identifier}</span>
                                </div>
                                <div className="w-full flex flex-col">
                                    <TextInput placeholder={translate('reports.description_placeholder')} className="outline-0 border-0 outline-none focus:outline-0 font-medium sm:text-lg -mt-2 w-full" value={metric.description} onChange={(e) => handleChangeDescription(metric, e.target.value)} />
                                </div>
                                <MinusIcon onClick={() => handleRemoveMetric(metric)} className="w-6 h-6 text-red-500 cursor-pointer ml-2 hover:text-red-400" />
                            </div>
                            <div className="flex flex-row w-full items-center gap-2">
                                <div className="flex flex-col w-full border-t border-gray-200 mt-2 pt-2">
                                    <p className="text-gray-700 text-sm font-bold">{translate('reports.event')}</p>
                                    <Select options={eventNames} selected={eventNames.find((eventName) => eventName.value === metric.name) || eventNames[0]} setSelected={(e) => handleSelectEventName(metric, e)} />
                                </div>
                            </div>
                            <MetricFilters report={report} setReport={setReport} metric={metric} index={index} />
                        </div>
                    ))}
                    <div className="flex flex-row justify-end mt-4 mx-4">
                        <Button onClick={() => save()}>{translate('save')}</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

const MetricFilters = ({ report, setReport, metric, index }) => {
    const { translate } = useTranslation();
    const [filters, setFilters] = useState(metric.filters || []);

    
    useEffect(() => {
        const newData = [...report.data];
        newData[index] = { ...newData[index], filters: filters };
        setReport({ ...report, data: newData });
    }, [filters]);

    const variableSourceOptions = [
        { id: 'event', name: translate('automatisation.from_event') },
        { id: 'contact', name: translate('automatisation.from_contact') }
    ];

    const setFilterSource = (source, index) => {
        const newFilters = [...filters];

        const newVariableValueWithoutSource = newFilters[index].variable.split(".").slice(1).join(".");
        newFilters[index] = { ...newFilters[index], source: source.id, variable: source.id + "." + newVariableValueWithoutSource };
        setFilters(newFilters);
    }

    const setFilterVariable = (variable, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], variable: newFilters[index].source + "." + variable };
        setFilters(newFilters);
    }

    const variableTypes = [
        { id: 1, name: translate('automatisation.string'), value: 'string' },
        { id: 2, name: translate('automatisation.number'), value: 'number' },
        { id: 3, name: translate('automatisation.boolean'), value: 'boolean' },
        { id: 4, name: translate('automatisation.string_variable'), value: 'string_variable' },
        { id: 5, name: translate('automatisation.number_variable'), value: 'number_variable' },
        { id: 6, name: translate('automatisation.boolean_variable'), value: 'boolean_variable' }
    ];

    const addFilter = () => {
        setFilters([...filters, { source: variableSourceOptions[0].id, variable: variableSourceOptions[0].id + ".", operation: "EQUALS", value: "", variable_type: variableTypes[0].value }]);
    }

    const getOperationOptions = (variableType) => {
        const operations = [
            { id: 1, name: translate('automatisation.is_equal'), value: 'EQUALS' },
            { id: 2, name: translate('automatisation.is_not_equal'), value: 'NOT_EQUALS' },
            ...(variableType === 'number' || variableType === 'number_variable' ? [{ id: 3, name: translate('automatisation.is_greater'), value: 'GREATER_THAN' },{ id: 4, name: translate('automatisation.is_less'), value: 'LESS_THAN' }] : []),
            ...(variableType === 'string' || variableType === 'string_variable' ? [{ id: 5, name: translate('automatisation.contains'), value: 'CONTAINS' }, { id: 6, name: translate('automatisation.does_not_contain'), value: 'NOT_CONTAINS' }] : []),
            ...(variableType === 'number_variable' || variableType === 'string_variable' || variableType === 'boolean_variable' ? [] : [{ id: 7, name: translate('automatisation.is_null_or_empty'), value: 'IS_NULL_OR_EMPTY' }, { id: 8, name: translate('automatisation.is_not_null_or_empty'), value: 'IS_NOT_NULL_OR_EMPTY' }]),
        ];
        return operations;
    };

    const setFilterOperation = (operation, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], operation: operation, variable_type: variableTypes.find(type => type.value === newFilters[index].variable_type)?.value || variableTypes[0].value };
        setFilters(newFilters);
    }

    const getSelectedFilterOperation = (filter) => {
        return getOperationOptions(filter.variable_type || variableTypes[0].value).find(operation => operation.value === filter.operation) || getOperationOptions(filter.variable_type || variableTypes[0].value)[0];
    }

    const setFilterVariableType = (variableType, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], variable_type: variableType, value: "", operation: "EQUALS" };
        setFilters(newFilters);
    }

    const setFilterValue = (value, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], value: newFilters[index].value_source + "." + value };
        setFilters(newFilters);
    }

    const setFilterValueWithoutSource = (value, index) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], value: value };
        setFilters(newFilters);
    }

    const setFilterValueSource = (source, index) => {
        const newFilters = [...filters];
        const newVariableValueWithoutSource = newFilters[index].value.split(".").slice(1).join(".");
        newFilters[index] = { ...newFilters[index], value_source: source.id, value: source.id + "." + newVariableValueWithoutSource };
        setFilters(newFilters);
    }

    const removeFilter = (index) => {
        const newFilters = [...filters];
        newFilters.splice(index, 1);
        setFilters(newFilters);
    }

    return (
        <div className="flex flex-col w-full mt-4 space-y-2">
            <div className="flex flex-row items-center gap-2 justify-between">
                <p className="text-md font-bold">{translate('reports.filters')}</p>
                <PlusIcon onClick={() => addFilter()} className="w-6 h-6 text-gray-500 cursor-pointer hover:text-gray-300" />
            </div>
            {filters.map((filter, filterIndex) => (
                <div key={"filter_" + filterIndex} className="grid grid-cols-11 items-center gap-2 border border-gray-200 rounded-md p-2">
                    <div className="col-span-11 flex flex-row items-center justify-between">
                        <span className="text-sm font-bold">{translate('reports.filter')}</span>
                        <MinusIcon onClick={() => removeFilter(filterIndex)} className="w-6 h-6 text-red-500 cursor-pointer ml-2 hover:text-red-400" />
                    </div>
                    <div className='col-span-5'>
                        <Select options={variableSourceOptions} selected={variableSourceOptions.find(source => source.id === filter.source) || variableSourceOptions[0]} setSelected={(e) => setFilterSource(e, filterIndex)} />
                    </div>
                    <div className="col-span-1 bg-gray-200 h-[1px]"></div>
                    <div className="col-span-5">
                        <TextInput placeholder={translate('automatisation.variable')} value={filter.variable?.split(".").slice(1).join(".")} onChange={(e) => setFilterVariable(e.target.value, filterIndex)} />
                    </div>
                    <div className='col-span-5'>
                        <Select options={getOperationOptions(filter.variable_type || variableTypes[0].value)} selected={getSelectedFilterOperation(filter)} setSelected={(e) => setFilterOperation(e.value, filterIndex)} />            
                    </div>
                    <div className="col-span-1 bg-gray-200 h-[1px]"></div>
                    <div className='col-span-5'>
                        <Select options={variableTypes} selected={variableTypes.find(type => type.value === filter.variable_type) || variableTypes[0]} setSelected={(e) => setFilterVariableType(e.value, filterIndex)} />
                    </div>
                    {(filter.variable_type === 'string_variable' || filter.variable_type === 'number_variable' || filter.variable_type === 'boolean_variable') && <div className="col-span-11 grid grid-cols-11 items-center gap-2">
                        <div className='col-span-5'>
                            <Select options={variableSourceOptions} selected={variableSourceOptions.find(source => source.id === filter.value_source) || variableSourceOptions[0]} setSelected={(e) => setFilterValueSource(e, filterIndex)} />
                        </div>
                        <div className="col-span-1 bg-gray-200 h-[1px]"></div>
                        
                        <div className='col-span-5'>
                            <TextInput value={filter.value.split(".").slice(1).join(".")} onChange={(e) => setFilterValue(e.target.value, filterIndex)} />
                        </div>
                    </div>}
                    {(!filter.variable_type?.includes('variable') && getSelectedFilterOperation(filter).value != 'IS_NULL_OR_EMPTY' && getSelectedFilterOperation(filter).value != 'IS_NOT_NULL_OR_EMPTY') && <div className="col-span-5 items-center gap-2">
                        <TextInput value={filter.value} onChange={(e) => setFilterValueWithoutSource(e.target.value, filterIndex)} />
                    </div>}
                </div>
            ))}
        </div>
    );
}

export const ReportChart = ({ report, results, isMonthly = false }) => {
    const { translate } = useTranslation();
    const { formatDate } = useDateFormat();
    const chartdata = results?.chart?.map((item) => ({
        ...item,
        date: isMonthly ? formatDate(item.start_date, "MMM", 0) : formatDate(item.start_date, "MMM dd", 0),

    }));
    const categories = report?.data?.map((metric) => metric.letter_identifier);

    const roundResult = (result) => {

        if (result % 1 === 0) {
            return result;
        }
        const decimalResult = result?.toFixed(2);
        return decimalResult;
    }

    if (report.display_type === 'metric') {
        const getMetricResult = (metric) => {
            if (report.type === 'funnel') {
                return results[metric.letter_identifier]?.contact_count;
            }
            return results[metric.letter_identifier]?.result;
        }
        return (
            <div className="flex flex-row h-full w-full divide-x divide-gray-200">
                {report.data.map((metric) => (
                    <div key={metric.letter_identifier} className="flex flex-col w-full h-full items-center justify-center">
                        <div className="flex flex-row items-center gap-2">
                            <div className="flex flex-row w-8 h-8 bg-gray-50 border border-gray-200 rounded-md items-center justify-center"  >
                                <span className="text-sm font-bold">{metric.letter_identifier}</span>
                            </div>
                            <div className="w-full flex flex-col">
                                <p className="text-sm font-bold">{metric.description}</p>
                            </div>
                        </div>
                        <p className="text-7xl font-bold mt-4">{roundResult(getMetricResult(metric))}</p>
                    </div>
                ))}
            </div>
        );
    } else if (report.display_type === 'bar') {
        return (
            <div className="flex flex-row h-full w-full">
                <BarChart
                    className="h-full"
                    data={chartdata}
                    index="date"
                    categories={categories}
                    valueFormatter={(number) =>
                        `${Intl.NumberFormat("us").format(number).toString()}`
                    }
                    onValueChange={(v) => console.log(v)}
                    xAxisLabel={translate('reports.date')}
                    yAxisLabel={translate('reports.value')}
                />

            </div>
        );
    } else if (report.display_type === 'bar_funnel') {
        const barFunnelData = report.data.map((metric) => ({
            ...metric,
            description: metric.description || metric.letter_identifier,
            contact_count: results[metric.letter_identifier]?.contact_count,
        }));
        return (
            <div className="flex flex-row h-full w-full">
                <BarChart
                    className="h-full"
                    data={barFunnelData}
                    index="description"
                    categories={["contact_count"]}
                    valueFormatter={(number) =>
                        `${Intl.NumberFormat("us").format(number).toString()}`
                    }
                    onValueChange={(v) => console.log(v)}
                    xAxisLabel={translate('reports.step')}
                    yAxisLabel={translate('reports.value')}
                />
            </div>
        );
    }
    return (
        <div className="flex flex-row h-full w-full">
            <LineChart
                className="h-full"
                data={chartdata}
                index="date"
                categories={categories}
                valueFormatter={(number) =>
                    `${Intl.NumberFormat("us").format(number).toString()}`
                }
                onValueChange={(v) => console.log(v)}
                xAxisLabel={translate('reports.date')}
                yAxisLabel={translate('reports.value')}
            />

        </div>
    );
}